import { zodResolver } from "@hookform/resolvers/zod";
import { Stack } from "@mui/material";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { StyledValidationText } from "Components/Shared/FormStyles";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { VatRegistryStatusText } from "Components/Shared/Vat/VatRegistryStatusText";
import { FieldEditForm } from "Components/UserAccount/Shared/PublicInformation/FieldEditForm";
import { usePartyFieldSave } from "Components/UserAccount/Shared/PublicInformation/usePartyFieldSave";
import type { PartyDto, Region } from "Infrastructure/Api/Api";
import { getVatNumberError, normalizeVatNumber } from "Schemas/vatNumberSchema";
import { Resources, type TFunction, useResource } from "Translations/Resources";
import { useForm } from "react-hook-form";
import { z } from "zod";

// The market comes from the party (an immutable snapshot), not from the form — unlike onboarding,
// where it is still being picked.
const createVatNumberSchema = (t: TFunction, region: Region) =>
  z
    .object({
      // Empty is allowed: whether the party is registered is the register's word, never the form's.
      // The save asks the register and may fill in the number the DIČ implies.
      vatNumber: z.string(),
    })
    .superRefine((values, ctx) => {
      const vatNumberError = getVatNumberError(t, { ...values, region });

      if (vatNumberError) {
        ctx.addIssue({
          code: "custom",
          message: vatNumberError,
          path: ["vatNumber"],
        });
      }
    });

type VatNumberFormValues = z.infer<ReturnType<typeof createVatNumberSchema>>;

type Props = {
  party: PartyDto;
  region: Region;
  // Gets the number as sent; the save may replace it with the one the register confirms.
  onSuccess: (sentVatNumber: string) => void;
};

/** VAT ID slice of the party edit (the "Registrace k DPH" section's dialog). */
export const VatNumberForm = ({ party, region, onSuccess }: Props) => {
  const { t } = useResource();
  const { vatNumber, vatStatus } = party;

  const {
    handleSubmit,
    control,
    formState: { isDirty, errors },
  } = useForm<VatNumberFormValues>({
    resolver: zodResolver(createVatNumberSchema(t, region)),
    defaultValues: { vatNumber },
  });

  const { saveParty, isSaving } = usePartyFieldSave(party, (sentParty) =>
    onSuccess(sentParty.vatNumber),
  );

  if (!party.publicID) {
    return <ErrorText>{t(Resources.Errors.MissingId)}</ErrorText>;
  }

  return (
    <FieldEditForm
      onSubmit={handleSubmit((data) =>
        saveParty({ vatNumber: data.vatNumber }),
      )}
      isSubmitDisabled={!isDirty}
      isSaving={isSaving}
    >
      {/* The number and the register's word on it are one question, so they share one error slot below
          the group instead of the helper line the input reserves by default; the always-rendered slot
          means nothing shifts when a message appears. */}
      <Stack gap={1}>
        <FormInput
          disableHelperText
          control={control}
          name="vatNumber"
          label={t(Resources.Common.VatNumber)}
          valueTransformer={normalizeVatNumber}
          autoFocus
        />
        {/* The register's word about the STORED number; nothing is asked while typing — the save verifies,
            and the section behind this dialog shows the answer once the party is refetched. An open question
            here is not an outage that just happened (as in onboarding) — most often nobody has asked yet. */}
        <VatRegistryStatusText
          vatRegistryStatus={vatStatus}
          vatNumber={vatNumber}
          openQuestionText={t(
            Resources.Components.Account.VatStatusNotVerifiedHint,
          )}
        />
        <StyledValidationText>{errors.vatNumber?.message}</StyledValidationText>
      </Stack>
    </FieldEditForm>
  );
};
